import { SimpleChange, SimpleChanges } from '@angular/core';
import { isEqual as _isEqual } from 'lodash-es';
import { Subject } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { Constructor } from './constructor';

export function WithChangesSubject<T extends Constructor<object>>(Base: T = (class { }) as T) {
  return class extends Base {
    constructor(...args: any[]) {
      super(args);
    }

    simpleChanges$ = new Subject<SimpleChanges>();

    /**
     * This must be called by using
     * super.ngOnChanges as the angular change detection
     * seems to ingore ngOnChanges on mixons
     *
     * @param simpleChanges
     */
    ngOnChanges(simpleChanges: SimpleChanges) {
      this.simpleChanges$.next(simpleChanges);
    }

    watchForChangeKeys(...keys: string[]) {
      return this.simpleChanges$.pipe(
        map(sc => this._checkIfChanged(sc, ...keys)),
        filter(sc => !!sc)
      );
    }

    private _checkIfChanged(simpleChanges: SimpleChanges, ...keys: string[]) {
      const changes = keys.map(key => ({ key, value: simpleChanges[key] }));

      // No keys
      if (changes.every(c => !c.value)) {
        return null;
      }

      // Ignore first change
      if (changes.every(c => c.value?.isFirstChange())) {
        return null;
      }

      const totalChanges = changes
        .filter(c => c.value)
        .filter(c => !this.areEqual(c.value))
        .map(({ key, value }) => ({ key, value: value.currentValue }));

      if (totalChanges.length === 0) {
        return null;
      }

      return totalChanges;
    }

    private areEqual(change: SimpleChange) {
      return _isEqual(change.currentValue, change.previousValue);
    }
  };
}
